import { createFileRoute } from "@tanstack/react-router"
import { z } from "zod"

import { finishAndGetNextStep, useOrderedSteps, useSessionDetail } from "@/services/dexie"

import { ClientRedirect } from "../../../../components/ClientRedirect"
import { SpinnerLayout } from "../../../../components/layouts/SpinnerLayout"
import { getStepRoute, LINKS } from "../../../../routing/links"
import { IntroContent } from "../../../../sections/intro/IntroContent"
import { IntroExpectedResults } from "../../../../sections/intro/IntroExpectedResults"
import { IntroRequirements } from "../../../../sections/intro/IntroRequirements"
import { introSubStepIds, useIntroSubSteps } from "../../../../sections/intro/useIntroSubSteps"

const pathParamsSchema = z.object({ step: z.enum(introSubStepIds) })

export const Route = createFileRoute("/$sessionId/_session/intro/$step")({
  params: {
    parse: (rawParams) => pathParamsSchema.parse(rawParams),
  },
  component: IntroStepRoute,
})

function IntroStepRoute() {
  const { step, sessionId } = Route.useParams()
  const { data: detail, isLoading: detailLoading } = useSessionDetail(sessionId)

  const { data: orderedSteps } = useOrderedSteps(sessionId)
  const introSubSteps = useIntroSubSteps(detail)

  const introStep = orderedSteps?.find((s) => s.name === "Intro")

  // Still loading Dexie
  if (detailLoading || !detail || !orderedSteps || orderedSteps.length === 0) return <SpinnerLayout />

  // Intro already completed — go to next unfinished step
  if (introStep?.isFinished) {
    const nextStep = orderedSteps.find((s) => !s.isFinished)
    if (!nextStep) return <ClientRedirect to={LINKS.finalCheck(sessionId)} />

    return <ClientRedirect to={getStepRoute(nextStep, sessionId)} />
  }

  const currentIndex = introSubSteps.findIndex((s) => s.id === step)
  const currentIntroSubStep = introSubSteps[currentIndex]

  // Valid step ID but not present in the current intro flow (e.g. "requirements" in URL but requirementsIntroScreenEnabled is off)
  // redirect to root route so correct step will be resolved
  if (!currentIntroSubStep) {
    return <ClientRedirect to={LINKS.intro(sessionId)} />
  }

  const nextIntroSubStep = introSubSteps[currentIndex + 1]

  // URL for the next step — either next intro sub-step or first real step after intro
  const nextStepAfterIntro = orderedSteps.find((s) => s.name !== "Intro" && !s.isFinished)
  const postIntroUrl = nextStepAfterIntro ? getStepRoute(nextStepAfterIntro, sessionId) : LINKS.finalCheck(sessionId)
  const nextUrl = nextIntroSubStep ? `${LINKS.intro(sessionId)}/${nextIntroSubStep.id}` : postIntroUrl

  // When leaving the last intro slide, mark Intro as finished in Dexie
  const isLastSlide = !nextIntroSubStep
  const beforeExit = isLastSlide ? () => finishAndGetNextStep("Intro", sessionId) : undefined

  if (currentIntroSubStep.isRequirements) {
    return <IntroRequirements nextUrl={nextUrl} beforeExit={beforeExit} />
  }

  if (currentIntroSubStep.isExpectedResults) {
    return <IntroExpectedResults nextUrl={nextUrl} beforeExit={beforeExit} />
  }

  return (
    <IntroContent
      icon={currentIntroSubStep.icon}
      title={currentIntroSubStep.title ?? ""}
      text={currentIntroSubStep.text ?? ""}
      currentStep={currentIndex}
      nextScreenUrl={nextUrl}
      stepCount={introSubSteps.length}
      hideBaseLayoutCounter
      beforeExit={beforeExit}
    />
  )
}
